// ─────────────────────────────────────────────────────────────────────────
// Facturación electrónica — módulo `/facturacion`.
//
// Reemplaza la pestaña "Emisiones ⚡" de Contabilidad y absorbe la nota de crédito
// que estaba en Emitir. Pestañas (con ?tab= en la URL, como Contabilidad):
//   emitidos      lo emitido desde el ERP, estado SII, PDF/XML, reenvío
//   por-vincular  facturas externas (Enternet) que el SII informó y no tienen OT
//   folios        folios disponibles por tipo y ambiente
//   notas         notas de crédito y anulación (solo admin y accountant)
// El ambiente (producción / certificación) se dice SIEMPRE arriba: emitir creyendo
// que es prueba cuando es producción es un documento real en el SII.
// Esconder una pestaña no protege: cada ruta del backend exige su rol.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import AuthContext from "../../AuthContext";
import backend from "../../backend";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";
import Emitidos from "./Emitidos";
import PorVincular from "./PorVincular";
import FoliosAmbiente from "./FoliosAmbiente";
import NotasYAnulacion from "./NotasYAnulacion";

const tabStyle = activa => ({
  background: activa ? "#1e2a38" : "transparent", border: `1px solid ${activa ? "#3498db" : "#333"}`,
  color: activa ? "#fff" : "#999", borderRadius: 999, padding: "6px 14px", fontSize: 12, fontWeight: 700,
  cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
});

const Facturacion = () => {
  useTitle("Facturación");
  const isMobile = useIsMobile();
  const auth = useContext(AuthContext);
  const roles = auth?.user?.roles || [];
  // Aviso corto cuando otra pantalla (p. ej. Emitir) redirige acá.
  const aviso = useLocation().state?.aviso;
  const puedeNotas = roles.some(r => ["admin", "accountant"].includes(r));

  const tabs = [
    { key: "emitidos", label: "Emitidos" },
    { key: "por-vincular", label: "Por vincular" },
    { key: "folios", label: "Folios" },
    ...(puedeNotas ? [{ key: "notas", label: "Notas y anulación" }] : []),
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const pedida = searchParams.get("tab");
  const tab = tabs.some(t => t.key === pedida) ? pedida : "emitidos";
  const irA = key => setSearchParams({ tab: key }, { replace: true });

  // Ambiente: lo informan las pestañas al cargar (esProduccion); null = aún no se sabe.
  const [esProduccion, setEsProduccion] = useState(null);
  const onAmbiente = useCallback(v => setEsProduccion(!!v), []);

  const [folios, setFolios] = useState(null);
  const [cargandoFolios, setCargandoFolios] = useState(false);
  const cargarFolios = useCallback(async (refrescar = false) => {
    setCargandoFolios(true);
    try {
      const { data } = await backend.get(`dte/folios${refrescar ? "?refrescar=1" : ""}`);
      setFolios(data);
      if (data.esProduccion != null) setEsProduccion(!!data.esProduccion);
      else if (data.ambiente) setEsProduccion(data.ambiente === "produccion");
    } catch (e) { setFolios({ folios: {}, errores: { 33: e?.response?.data?.error || e.message } }); }
    setCargandoFolios(false);
  }, []);
  useEffect(() => { cargarFolios(); }, [cargarFolios]);

  return (
    <div style={{ padding: isMobile ? "12px 16px" : "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <h2 className="mv-screen-title" style={{ margin: 0 }}>Facturación electrónica</h2>
        <span style={{ fontSize: 12, fontWeight: 700, color: esProduccion == null ? "#666" : esProduccion ? "#e74c3c" : "#f39c12" }}>
          {esProduccion == null ? "…" : esProduccion ? "⬤ PRODUCCIÓN — documentos reales" : "○ certificación (ambiente de prueba)"}
        </span>
      </div>

      {aviso && <div style={{ fontSize: 12, color: "#f39c12" }}>{aviso}</div>}

      <div className="chips-scroll" style={{ display: "flex", gap: 6, overflowX: "auto" }}>
        {tabs.map(t => <button key={t.key} onClick={() => irA(t.key)} style={tabStyle(tab === t.key)}>{t.label}</button>)}
      </div>

      {tab === "emitidos" && <Emitidos roles={roles} onAmbiente={onAmbiente} />}
      {tab === "por-vincular" && <PorVincular roles={roles} onAmbiente={onAmbiente} />}
      {tab === "folios" && <FoliosAmbiente data={folios} cargando={cargandoFolios} onRefrescar={() => cargarFolios(true)} roles={roles} />}
      {tab === "notas" && puedeNotas && <NotasYAnulacion roles={roles} />}
    </div>
  );
};

export default Facturacion;
